import { LetterCloseIcon } from "assets";
import { ContactPerformerPopUp } from "component-library";
import { SubmitFormEndpoint } from "component-library/src/components/Organisms/ContactPerformerPopUp/PerformerContact";
import { UserClaimTypes, UserManager } from "core";
import { useTranslation } from "i18n";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import useGetReps from "pages/Profile/hooks/useGetReps";
import {
  checkIsAgent,
  checkIsCD,
  getUserAttribute,
  UserAttributes,
} from "shared-auth";
import { dataTestIds } from "data-testids";
import { PROFILE_CONTACT_FULL_PATH, SELF_REP_SL_EMAIL } from "shared/constants";
import { useToggle } from "usehooks-ts";
import { displayAgentLists, SELF_REPRESENTED_REF_NUMBER } from "./helper";
import { heapTrackingForMainPageEmailProfile } from "app/helpers/heapTracking";

interface Props {
  isEdit?: boolean;
}

const RepsSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const [isContactModalOpen, toggleContactModal] = useToggle();
  const {
    agents = [],
    schools = [],
    isSelfRepresented,
    isLookingForRepresentation,
    isLoading,
  } = useGetReps();

  const isAgent = checkIsAgent();
  const isCd = checkIsCD();

  const seekingRepresentation = isAgent && isLookingForRepresentation;

  const agentLists = [
    ...agents,
    ...schools.map((school) => ({
      ...school,
      name: t("performer:profile.training.title.named", { name: school.name }),
    })),
  ];

  const selfRepresentedTrainingsList = agentLists.filter(
    (agent) => agent.refNumber !== SELF_REPRESENTED_REF_NUMBER
  );

  const handleOpenContactPerformerPopUp = () => {
    toggleContactModal();
    heapTrackingForMainPageEmailProfile(true);
  };

  return (
    <ProfileCard
      className="grid grid-cols-1 gap-4 text-s"
      isEdit={!isAgent && isEdit}
      editToPath={PROFILE_CONTACT_FULL_PATH}
      isLoading={isLoading}
      data-testid={
        dataTestIds.profile["Profile.ProfileCard.representationSection"]
      }
    >
      {isSelfRepresented && (
        <div
          data-testid={`${dataTestIds.profile["Profile.Agency.selfRepresentation"]}`}
        >
          <h2 className="text-s font-semibold leading-5">
            {t("performer:profile.representation.selfRepresented")}
          </h2>
          {seekingRepresentation && (
            <p className="text-s">
              {t("performer:profile.representation.seekingRepresentation")}
            </p>
          )}
          {seekingRepresentation || isCd ? (
            <button
              className="flex items-center gap-2 font-medium text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
              onClick={handleOpenContactPerformerPopUp}
            >
              <LetterCloseIcon width={13} className="shrink-0" />
              {t("performer:profile.representation.button.contact")}
            </button>
          ) : (
            <a
              href={`mailto:${SELF_REP_SL_EMAIL}`}
              className="mt-1 flex items-center gap-2 font-medium text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
            >
              <LetterCloseIcon width={13} className="shrink-0" />
              {SELF_REP_SL_EMAIL}
            </a>
          )}
          {selfRepresentedTrainingsList.length > 0 && (
            <>
              <div className="mb-4 border-b border-white/10 pb-1.5 last:border-b-0" />
              <div className="flex flex-col gap-4">
                {displayAgentLists(selfRepresentedTrainingsList)}
              </div>
            </>
          )}
        </div>
      )}
      {!isSelfRepresented && displayAgentLists(agentLists)}

      <ContactPerformerPopUp
        isOpen={isContactModalOpen}
        closeForm={toggleContactModal}
        performerName={UserManager.getClaimWithDefault(
          UserClaimTypes.profileFullName
        )}
        senderName={getUserAttribute(UserAttributes.FullName)}
        senderEmail={getUserAttribute(UserAttributes.Email)}
        performerId={UserManager.userId}
        canAttachFiles={isCd}
        emailSubjectLine={
          isAgent
            ? t("performer:profile.representation.fallbackEmailSubject")
            : undefined
        }
        submitformEndpoint={
          isAgent
            ? SubmitFormEndpoint.LookingForRep
            : SubmitFormEndpoint.SelfRep
        }
      />
    </ProfileCard>
  );
};

export default RepsSection;
